import { getTooltipAttributes } from "@common/contexts/Tooltip";
import { getTourAttributes } from "@common/contexts/Tour";
import Collapsible from "@common/controls/Collapsible/component";
import Icon, { IconAction, type IconActionSize } from "@common/controls/Icon/component";
import { type KeyboardNavigationMode } from "@common/hocs/keyboardNavigation";
import withDropdownHOC, {
  type WithDropdownCmpProps,
  type WithDropdownComponentAddedProps,
  type WithDropdownToggleProps
} from "@common/hocs/withDropdown";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import withDynamicOptionsHOC, { type WithDynamicOptionsAddedProps } from "@common/hocs/withDynamicOptions";
import { withLazyHOC } from "@common/hocs/withLazy";
import withValue from "@common/hocs/withValue";
import { sort } from "@common/lib/array";
import { useLoadingOptions } from "@common/lib/hooks";
import { Common } from "@common/types";
import classnames from "classnames";
import { groupBy, isArray, omit } from "lodash";
import * as React from "react";
import {
  ContentBase,
  type ContentHeight,
  type SelectHeading,
  SelectStaticContent,
  type SelectStaticContentProps,
  type SortOrder,
  Toggle
} from "./Base/component";
import { SelectOptionDescription } from "./SelectOptionDescription/component";
import withAddHOC, { type WithAddHOCBaseProps } from "./hocs/withAdd";
import withSearchTextHOC, { withSearchHOC } from "./hocs/withSearch";
import withStaticHOC, { type LazySelectComponentProps } from "./hocs/withStatic";
import {
  EMPTY_OPTION,
  calculateMaxHeight,
  getAvailableOptions,
  getSelectedLabel,
  getSelectedOption,
  isSameValue,
  optionItemComparer
} from "./lib";

import { IconType } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import "./style.less";

export type ValueType = string | number | boolean;

export type OptionItem<T> = Common.OptionItem<T>;

export interface WithOptions<TOption> {
  options: TOption[];
}

export type SelectGroupingOptions = {
  /**
   * by default single group is hidden
   */
  alwaysDisplay?: boolean;
  mode?: "accordion" | "plain";
  sort?: SortOrder;
};
export type SelectContentProps<T = any, TOption extends OptionItem<T> = OptionItem<T>> = Common.InputTypeProps<
  T | TOption | null | undefined,
  T | TOption
> &
  WithOptions<TOption> & {
    allOptions: TOption[];
    emptyDescriptions?: Common.OptionItemDescription[];
    emptyLabel?: string;
    emptyValue?: T;
    excludeOptionValues?: T[];
    grouping?: SelectGroupingOptions;
    heading?: SelectHeading | SelectHeading[];
    height?: ContentHeight;
    includeOptionValues?: T[];
    isEmptyLabelFaded?: boolean;
    isNoEmptyOption?: boolean;
    isRequired?: boolean;
    keyboardNavigationMode?: KeyboardNavigationMode;
    optionGoToTourStep?: string;
    outputType?: SelectOutputType;
    placeholder?: string;
    renderItemAddon?: (item: TOption) => any;
    renderItemLabel?: (item: TOption) => any;
    sort?: SortOrder;
    sortType?: Common.SortType;
    style?: React.CSSProperties;
  };

export type SelectOutputType = "option" | "value";

export type SelectPropsBase<T = any, TOption extends OptionItem<T> = OptionItem<T>> = SelectContentProps<T, TOption> & {
  blankOptionLabel?: any;
  children?: any;
  className?: string;
  icon?: string;
  iconSize?: number;
  isDisabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  name?: string;
  noArrow?: boolean;
  onClick?: () => void;
  selectedLabel?: any;
  tabIndex?: number;
  theme?: "borderless";
  toggle?: any;
  tooltip?: string;
};

export type SelectProps<T = any, TOption extends OptionItem<T> = OptionItem<T>> = SelectPropsBase<T, TOption> &
  Omit<WithDropdownComponentAddedProps, "align" | "allOptions"> & {
    align?: ContentAlign;
    tourStep?: string;
  };

const SelectToggle = React.memo(
  <T, TOption extends OptionItem<T>>(props: WithDropdownToggleProps & SelectProps<T, TOption>) => {
    const i18n = useI18n();

    const options = getAvailableOptions(props);
    const selectedOption = getSelectedOption(props);
    const selectedLabel = getSelectedLabel(i18n, options, props);
    useLoadingOptions(props.isLoading ?? false, props.allOptions);

    return (
      <Toggle
        {...props}
        isNoOptions={!options.length}
        onClick={
          props.isDisabled ||
          props.isLoading ||
          (!options.length && (!props.heading || (Array.isArray(props.heading) && !props.heading.length)))
            ? undefined
            : props.onClick ?? props.toggleOpen
        }
        className={classnames(props.className, {
          faded: props.isEmptyLabelFaded && selectedLabel === props.emptyLabel
        })}
        prepend={selectedOption?.prepend}
        title={selectedLabel}
      />
    );
  }
);

type SelectContentState = {
  expandedGroups: Common.GenericMap<null>;
};

export class SelectContent<T = any, TOption extends OptionItem<T> = OptionItem<T>> extends React.PureComponent<
  WithDropdownCmpProps & SelectContentProps<T, TOption> & LazySelectComponentProps,
  SelectContentState
> {
  public state: SelectContentState = {
    expandedGroups: {}
  };

  public render() {
    let options = getAvailableOptions(this.props).slice();
    if (this.props.sort)
      options = sort(options, {
        key: "label",
        order: this.props.sort,
        type: this.props.sortType ?? "natural"
      });

    const groupped = groupBy(options, (x) => x.group ?? "");
    let groupKeys = Object.keys(groupped);
    if (this.props.grouping?.sort) {
      groupKeys = groupKeys.sort((a, b) => a.localeCompare(b) * (this.props.grouping!.sort === "asc" ? 1 : -1));
    }
    const groupingTheme = this.props.grouping?.mode ?? "plain";

    return (
      <ContentBase
        className={classnames(`grouping_${groupingTheme}`)}
        keyboardNavigationMode={this.props.keyboardNavigationMode}
        heading={this.props.heading}
        height={this.props.height}
        isOpen={this.props.isOpen}
        open={this.props.open}
        close={this.props.close}
        options={this.props.options}
        onItemSelected={this._onItemSelected}
        toggleOpen={this.props.toggleOpen}
        hasMore={this.props.hasMore}
        loadMore={this.props.loadMore}
      >
        <>
          {!this.props.isRequired &&
            !this.props.isNoEmptyOption &&
            this._renderOptions(
              [
                {
                  descriptions: this.props.emptyDescriptions,
                  label: this.props.emptyLabel ?? EMPTY_OPTION.label,
                  parentValue: null,
                  value: this.props.emptyValue ?? EMPTY_OPTION.value,
                  isFaded: this.props.isEmptyLabelFaded
                }
              ],
              true
            )}

          {groupKeys.map((group, i) =>
            group && (groupKeys.length > 1 || this.props.grouping?.alwaysDisplay) ? (
              <div className="group" key={group}>
                <div className="group_content" onClick={this._getOnToggleExpanded(group)}>
                  <span
                    className={classnames("caption single-line-ellipsis", {
                      hint: groupingTheme === "plain"
                    })}
                    title={group}
                  >
                    {groupped[group][0].groupPrepend}
                    {group}
                  </span>
                  {groupingTheme === "accordion" && (
                    <Icon
                      icon={
                        group in this.state.expandedGroups ? Common.IconType.ChevronUp : Common.IconType.ChevronDown
                      }
                    />
                  )}
                </div>

                <Collapsible
                  className="children"
                  isCollapsed={!(group in this.state.expandedGroups) && groupingTheme !== "plain"}
                  isLazy
                >
                  {this._renderOptions(groupped[group], false, groupingTheme === "plain" ? 1 : 2)}
                </Collapsible>
              </div>
            ) : (
              this._renderOptions(groupped[group], false)
            )
          )}
        </>
      </ContentBase>
    );
  }

  private _getOnToggleExpanded(group: string) {
    return () => {
      if (group in this.state.expandedGroups) {
        this.setState({
          expandedGroups: omit(this.state.expandedGroups, group)
        });
      } else {
        this.setState({
          expandedGroups: { ...this.state.expandedGroups, [group]: null }
        });
      }
    };
  }

  private _renderOptions = (options: OptionItem<any>[], isEmpty: boolean, level: number = 1) =>
    options.map((opt, i) => (
      <React.Fragment key={opt.value}>
        <div
          className={classnames("item", {
            disabled: opt.isDisabled,
            faded: opt.isFaded,
            selected: isSameValue(this.props.value, opt.value)
          })}
          tabIndex={0}
          onClick={
            opt.isDisabled
              ? undefined
              : () => this._onItemSelected(this.props.outputType === "option" ? opt : opt.value)
          }
          data-name={opt.value ?? ""}
          {...getTooltipAttributes(opt.isDisabled ? opt.disabledTooltip : undefined)}
          {...getTourAttributes({ goToStep: this.props.optionGoToTourStep })}
        >
          <span className="item-content" style={{ "--level": level } as any}>
            <div className="info">
              {opt.prepend}
              {this.props.renderItemLabel && !isEmpty ? (
                <span className="content single-line-ellipsis">{this.props.renderItemLabel(opt as TOption)}</span>
              ) : (
                <span
                  className="content single-line-ellipsis"
                  dangerouslySetInnerHTML={{ __html: opt.label || "\u00A0" }}
                  title={opt.label}
                />
              )}
              {this.props.renderItemAddon && this.props.renderItemAddon(opt as TOption)}
            </div>

            {opt.descriptions ? <SelectOptionDescription descriptions={opt.descriptions} isSingleLine /> : null}
          </span>
        </div>
        {opt.isSeparated && <div className="separator"></div>}
      </React.Fragment>
    ));

  private _onItemSelected = (value: T | TOption) => {
    value !== this.props.value && this.props.onChange(value);
    this.props.close();
  };
}

export const SelectDropdown = withDropdownHOC<SelectProps<any, any>>(SelectToggle, SelectContent);

const SelectLazyBase = React.memo(
  <T, TOption extends OptionItem<T>>(props: SelectProps<T, TOption> & LazySelectComponentProps) => (
    <SelectDropdown {...props} align={props.align || ContentAlign.Stretch} />
  )
);

export const SelectLazy = withLazyHOC(SelectLazyBase, optionItemComparer);

const SelectStatic = withStaticHOC(SelectLazyBase);

export const Select = withSearchHOC(
  withSearchTextHOC<SelectProps<any, any>>(
    withValue<string | null | undefined, SelectProps>(
      <T, TOption extends OptionItem<T>>({
        alwaysShowHeading,
        heading,
        dropdownClassName,
        maxHeight,
        ...props
      }: SelectProps<T, TOption> & { alwaysShowHeading?: boolean }) => {
        const _heading = React.useMemo(() => {
          if (!heading) return;
          let result = isArray(heading) ? heading : [heading];
          if (props.allOptions?.length < 10 && !alwaysShowHeading) {
            result = result.filter((x) => !x.isSearch);
          }
          return result;
        }, [alwaysShowHeading, heading, props.allOptions]);

        return (
          <SelectStatic
            {...props}
            dropdownClassName={classnames("select_dropdown", dropdownClassName)}
            heading={_heading}
            maxHeight={calculateMaxHeight(maxHeight, _heading)}
          />
        );
      }
    )
  ),
  0
);
export default Select;

export type DynamicSelectProps = Omit<SelectProps<any, any>, "allOptions"> &
  WithDynamicOptionsAddedProps<OptionItem<ValueType>>;
const DynamicSelect = withDynamicOptionsHOC<any, Omit<SelectProps<any, any>, "allOptions">>(Select);

const DynamicSelectWithAddBase = withAddHOC<
  Omit<SelectProps<any, any>, "allOptions" | "options"> & WithDynamicOptionsAddedProps<OptionItem<any>>
>(withDynamicOptionsHOC<OptionItem<ValueType>, SelectProps<any, any>>(Select));
const SelectWithAddBase = withAddHOC<SelectProps<any, any>>(Select);
export type DynamicSelectWithAddProps<T = any, TOption extends OptionItem<T> = OptionItem<T>> = Omit<
  SelectProps<T, TOption>,
  "allowToggleEmpty" | "options"
> &
  WithDynamicOptionsAddedProps<OptionItem<any>> &
  WithAddHOCBaseProps;
const DynamicSelectWithAdd = <T, TOption extends OptionItem<T>>({
  dropdownClassName,
  ...props
}: DynamicSelectWithAddProps<T, TOption>) => (
  <DynamicSelectWithAddBase
    {...props}
    dropdownClassName={classnames(dropdownClassName, "with_add")}
    dropdownName={props.qa || props.name ? `${props.qa || props.name}_dropdown` : undefined}
  />
);
const SelectWithAdd = <T, TOption extends OptionItem<T>>({
  dropdownClassName,
  ...props
}: SelectProps<T, TOption> & WithAddHOCBaseProps) => (
  <SelectWithAddBase
    {...props}
    dropdownClassName={classnames(dropdownClassName, "with_add")}
    dropdownName={props.qa || props.name ? `${props.qa || props.name}_dropdown` : undefined}
    isNoEmptyOption
  />
);
export { DynamicSelect, DynamicSelectWithAdd, SelectWithAdd };

type SelectStaticIconProps = SelectStaticContentProps & {
  icon: IconType;
  iconSize: IconActionSize;
  isDisabled?: boolean;
};
export const SelectStaticIcon = withDropdownHOC<SelectStaticIconProps>(
  (props: SelectStaticIconProps & WithDropdownToggleProps) => (
    <IconAction
      ref={props.toggleRef}
      onClick={(e) => (e.stopPropagation(), props.toggleOpen())}
      icon={props.icon}
      isDisabled={props.isDisabled}
      size={props.iconSize}
    />
  ),
  (props: SelectStaticIconProps & WithDropdownCmpProps) => <SelectStaticContent {...props} />
);
